"use client"
import type React from "react"
import { useState, useEffect, forwardRef, useImperativeHandle } from "react"
import InputField from "../../../components/common/InputField"
import {
  CurrencyDollarIcon,
  ExclamationCircleIcon,
  ClockIcon,
  ChartBarIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  StarIcon
} from "@heroicons/react/24/outline"
import { createCoursePrice, updateCoursePrice, getAllGstRates, getCoursePriceByCourseId } from "../../../api/courseService"
import useAuth from "../../../hooks/useAuth"
import Button from "../../../components/common/Button"

interface PaymentDetailsStepProps {
  data: any
  onChange: (data: any) => void
  isEditMode?: boolean
}

export interface PaymentDetailsStepRef {
  savePaymentDetails: () => Promise<void>;
  hasUnsavedChanges: () => boolean;
}

interface GstRate {
  _id: string;
  gstPercent: number;
}

interface PriceDetail {
  _id?: string;
  price: number;
  actualPrice: number;
  validityType: string;
  validityInDays: number;
  gstRateId: string;
  _delete?: boolean;
  isBestDeal?: boolean; // Keep this for UI purposes
}

const PaymentDetailsStep = forwardRef<PaymentDetailsStepRef, PaymentDetailsStepProps>(({ data, onChange, isEditMode = false }, ref) => {
  const { user } = useAuth();
  const [courseValidity, setCourseValidity] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("3 months");
  const [customDays, setCustomDays] = useState("");
  const [basePrice, setBasePrice] = useState("");
  const [actualPrice, setActualPrice] = useState("");
  const [gstAmount, setGstAmount] = useState("");
  const [gstRates, setGstRates] = useState<GstRate[]>([]);
  const [selectedGstRate, setSelectedGstRate] = useState<GstRate | null>(null);
  const [loadingGstRates, setLoadingGstRates] = useState(false);
  const [priceDetails, setPriceDetails] = useState<PriceDetail[]>([]);
  const [, setLoading] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [pricingId, setPricingId] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isBestDeal, setIsBestDeal] = useState(false);

  const periods = ["1 month", "3 months", "6 months", "1 year", "Custom (in days)"];

  const validityMap = {
    "1 month": "1month",
    "3 months": "3months",
    "6 months": "6months",
    "1 year": "1year",
    "Custom (in days)": "custom"
  };

  useEffect(() => {
    if (data.pricing && data.pricing.priceDetail && Array.isArray(data.pricing.priceDetail)) {
      setPricingId(data.pricing._id || null);
      
      const existingPriceDetails = data.pricing.priceDetail.map((detail: any) => ({
        _id: detail._id,
        price: detail.price,
        actualPrice: detail.actualPrice,
        validityType: detail.validityType,
        validityInDays: detail.validityInDays,
        gstRateId: detail.gstRateId._id || detail.gstRateId,
        isBestDeal: detail.isBestDeal || false
      }));

      setPriceDetails(existingPriceDetails);

      if (existingPriceDetails.length > 0) {
        setCourseValidity(true);
      }
    } else {
      setPriceDetails([]);
      setCourseValidity(false);
      setPricingId(null);
    }
    setHasUnsavedChanges(false);
  }, [data.pricing, data._id]);

  useEffect(() => {
    const refreshPricingData = async () => {
      if (data._id) {
        try {
          const pricingResponse = await getCoursePriceByCourseId(data._id);
          if (pricingResponse.status === 200 && pricingResponse.data) {
            onChange({
              ...data,
              pricing: pricingResponse.data
            });

            setPricingId(pricingResponse.data._id);

            const existingPriceDetails = pricingResponse.data.priceDetail.map((detail: any) => ({
              _id: detail._id,
              price: detail.price,
              actualPrice: detail.actualPrice,
              validityType: detail.validityType,
              validityInDays: detail.validityInDays,
              gstRateId: detail.gstRateId._id || detail.gstRateId,
              isBestDeal: detail.isBestDeal || false
            }));

            setPriceDetails(existingPriceDetails);

            if (existingPriceDetails.length > 0) {
              setCourseValidity(true);
            }
          }
        } catch (error) {
          console.error("Error refreshing pricing data:", error);
        }
      }
    };

    refreshPricingData();
  }, [data._id, isEditMode]);

  useEffect(() => {
    const fetchGstRates = async () => {
      setLoadingGstRates(true);
      try {
        const response = await getAllGstRates();
        if (response.status === 200 && response.data) {
          setGstRates(response.data);
          const defaultGst = response.data.find((rate: GstRate) => rate.gstPercent === 12);
          if (defaultGst) {
            setSelectedGstRate(defaultGst);
          }
        }
      } catch (error: any) {
        console.error("Error fetching GST rates:", error);
      } finally {
        setLoadingGstRates(false);
      }
    };
    fetchGstRates();
  }, []);

  useEffect(() => {
    if (basePrice && selectedGstRate) {
      const gstValue = (parseFloat(basePrice) * selectedGstRate.gstPercent) / 100;
      setGstAmount(gstValue.toFixed(2));
    } else {
      setGstAmount("");
    }
  }, [basePrice, selectedGstRate]);

  const getDaysFromPeriod = (period: string): number | undefined => {
    switch (period) {
      case "1 month": return 30;
      case "3 months": return 90;
      case "6 months": return 180;
      case "1 year": return 365;
      case "Custom (in days)": return customDays ? Number(customDays) : undefined;
      default: return undefined;
    }
  };

  const totalPrice = (parseFloat(basePrice) || 0) + (parseFloat(gstAmount) || 0);

  const hasValidFormData = () => {
    // Check if required fields are filled
    if (!basePrice || !actualPrice || !selectedGstRate || !courseValidity) {
      return false;
    }
    
    // Check if prices are valid numbers and not negative
    const basePriceNum = parseFloat(basePrice);
    const actualPriceNum = parseFloat(actualPrice);
    
    if (isNaN(basePriceNum) || isNaN(actualPriceNum)) {
      return false;
    }
    
    if (basePriceNum < 0 || actualPriceNum < 0) {
      return false;
    }
    
    return true;
  };

  const calculateDiscount = (detail: PriceDetail) => {
    if (!detail.price || !detail.actualPrice) return 0;
    const base = detail.price;
    const actual = detail.actualPrice;
    if (base <= actual) return 0;
    return Math.round(((base - actual) / base) * 100);
  };

  const editPriceDetail = (index: number) => {
    const detail = priceDetails[index];
    setBasePrice(detail.price.toString());
    setActualPrice(detail.actualPrice.toString());

    const gstRate = gstRates.find(rate => rate._id === detail.gstRateId);
    if (gstRate) {
      setSelectedGstRate(gstRate);
    }

    const period = Object.entries(validityMap).find(
      ([_, value]) => value === detail.validityType
    )?.[0] || "3 months";
    setSelectedPeriod(period);

    if (detail.validityInDays && detail.validityType === "custom") {
      setCustomDays(detail.validityInDays.toString());
    }

    setIsBestDeal(detail.isBestDeal || false);
    setEditingIndex(index);
    setHasUnsavedChanges(true);
  };

  const updatePriceDetail = () => {
    if (editingIndex === null || !hasValidFormData()) {
      if (basePrice && actualPrice && (parseFloat(basePrice) < 0 || parseFloat(actualPrice) < 0)) {
        alert("Price values cannot be negative");
      } else {
        alert("Please fill all required fields with valid positive values");
      }
      return;
    }

    const validityType = validityMap[selectedPeriod as keyof typeof validityMap];
    const validityInDays = getDaysFromPeriod(selectedPeriod);

    if (!validityType || !validityInDays) {
      alert("Please select a valid validity period");
      return;
    }

    const updatedPriceDetails = [...priceDetails];

    if (updatedPriceDetails[editingIndex]._id) {
      updatedPriceDetails[editingIndex] = {
        _id: updatedPriceDetails[editingIndex]._id,
        price: parseFloat(basePrice),
        actualPrice: parseFloat(actualPrice),
        validityType,
        validityInDays,
        gstRateId: selectedGstRate?._id || "",
        isBestDeal: isBestDeal
      };
    } else {
      updatedPriceDetails[editingIndex] = {
        price: parseFloat(basePrice),
        actualPrice: parseFloat(actualPrice),
        validityType,
        validityInDays,
        gstRateId: selectedGstRate?._id || "",
        isBestDeal: isBestDeal
      };
    }

    setPriceDetails(updatedPriceDetails);
    setBasePrice("");
    setActualPrice("");
    setCustomDays("");
    setSelectedPeriod("3 months");
    setIsBestDeal(false);
    setEditingIndex(null);
    setHasUnsavedChanges(false);
  };

  const addPriceDetail = () => {
    if (!hasValidFormData()) {
      if (basePrice && actualPrice && (parseFloat(basePrice) < 0 || parseFloat(actualPrice) < 0)) {
        alert("Price values cannot be negative");
      } else {
        alert("Please fill all required fields with valid positive values before adding a price option");
      }
      return;
    }

    const validityType = validityMap[selectedPeriod as keyof typeof validityMap];
    const validityInDays = getDaysFromPeriod(selectedPeriod);

    if (!validityType || !validityInDays) {
      alert("Please select a valid validity period");
      return;
    }

    const newPriceDetail: PriceDetail = {
      price: parseFloat(basePrice),
      actualPrice: parseFloat(actualPrice),
      validityType,
      validityInDays,
      gstRateId: selectedGstRate?._id || "",
      isBestDeal: isBestDeal
    };

    setPriceDetails([...priceDetails, newPriceDetail]);
    setBasePrice("");
    setActualPrice("");
    setCustomDays("");
    setSelectedPeriod("3 months");
    setIsBestDeal(false);
    setHasUnsavedChanges(false);
  };

  const removePriceDetail = (index: number) => {
    const updatedPriceDetails = [...priceDetails];

    if (updatedPriceDetails[index]._id) {
      updatedPriceDetails[index] = {
        ...updatedPriceDetails[index],
        _delete: true
      };
    } else {
      updatedPriceDetails.splice(index, 1);
    }

    setPriceDetails(updatedPriceDetails);
    setHasUnsavedChanges(true);
  };

  const cancelEdit = () => {
    setBasePrice("");
    setActualPrice("");
    setCustomDays("");
    setSelectedPeriod("3 months");
    setIsBestDeal(false);
    setEditingIndex(null);
    setHasUnsavedChanges(false);
  };

  const savePaymentDetails = async () => {
    try {
      setLoading(true);

      if (isEditMode && hasUnsavedChanges && hasValidFormData()) {
        const shouldSave = window.confirm(
          "You have unsaved changes in the form. Do you want to add them as a new price option before continuing?"
        );

        if (shouldSave) {
          if (editingIndex !== null) {
            updatePriceDetail();
          } else {
            addPriceDetail();
          }
          await new Promise(resolve => setTimeout(resolve, 100));
        } else {
          cancelEdit();
        }
      }

      const activePriceDetails = priceDetails.filter(detail => !detail._delete);

      if (activePriceDetails.length === 0) {
        throw new Error("Please add at least one price option");
      }

      if (!user?._id) {
        throw new Error("User authentication failed");
      }

      let formattedPriceDetails: any[];

      if (pricingId && isEditMode) {
        formattedPriceDetails = priceDetails.map(detail => {
          if (detail._id && detail._delete) {
            return {
              _id: detail._id,
              _delete: true
            };
          }

          if (detail._id && !detail._delete) {
            return {
              _id: detail._id,
              price: detail.price,
              actualPrice: detail.actualPrice,
              validityType: detail.validityType,
              validityInDays: detail.validityInDays,
              gstRateId: detail.gstRateId
              // isBestDeal removed from API call
            };
          }

          if (!detail._id && !detail._delete) {
            return {
              price: detail.price,
              actualPrice: detail.actualPrice,
              validityType: detail.validityType,
              validityInDays: detail.validityInDays,
              gstRateId: detail.gstRateId
              // isBestDeal removed from API call
            };
          }

          return detail;
        }).filter(detail =>
          (detail.hasOwnProperty('price') && !detail._delete) || detail._delete
        );
      } else {
        formattedPriceDetails = priceDetails
          .filter(detail => !detail._delete)
          .map(detail => ({
            price: detail.price,
            actualPrice: detail.actualPrice,
            validityType: detail.validityType,
            validityInDays: detail.validityInDays,
            gstRateId: detail.gstRateId
            // isBestDeal removed from API call
          }));
      }

      let response;

      if (pricingId && isEditMode) {
        response = await updateCoursePrice({
          id: pricingId,
          user_id: user._id,
          priceDetail: formattedPriceDetails as any
        });
      } else {
        response = await createCoursePrice({
          courseId: data._id,
          createdBy: user._id,
          priceDetail: formattedPriceDetails as any
        });

        if (response.data && response.data._id) {
          setPricingId(response.data._id);
        }
      }

      if (response.status >= 400) {
        throw new Error(response.message || "Failed to save pricing");
      }

      const updatedData = {
        ...data,
        pricing: response.data
      };

      onChange(updatedData);
      setHasUnsavedChanges(false);
    } catch (error: any) {
      console.error("Error saving payment details:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    savePaymentDetails,
    hasUnsavedChanges: () => hasUnsavedChanges
  }));

  const formatValidityType = (validityType: string) => {
    switch (validityType) {
      case "1month": return "1 Month";
      case "3months": return "3 Months";
      case "6months": return "6 Months";
      case "1year": return "1 Year";
      case "custom": return "Custom";
      default: return validityType;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
        <div className="flex items-center mb-2">
          <div className="bg-green-100 rounded-full p-1.5 mr-2">
            <CurrencyDollarIcon className="text-green-600 w-5 h-5" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Set Your Course Pricing</h2>
        </div>
        <p className="text-gray-600 text-sm">Configure pricing, validity, and payment options to maximize your course revenue and accessibility.</p>
        {hasUnsavedChanges && (
          <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-700 text-sm flex items-center">
              <ExclamationCircleIcon className="w-4 h-4 mr-2" />
              You have unsaved changes. Please save or cancel before continuing.
            </p>
          </div>
        )}
      </div>

      {/* Course Validity */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center justify-between">
          <div className="flex items-start">
            <div className="bg-orange-100 rounded-full p-1.5 mr-2 mt-0.5">
              <ClockIcon className="text-orange-600 w-4 h-4" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Course Validity Period</h3>
              <p className="text-xs text-gray-600 max-w-md">
                Set access duration for your course. When disabled, students get lifetime access.
                Limited access can create urgency and higher engagement.
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer ml-4">
            <input
              type="checkbox"
              checked={courseValidity}
              onChange={(e) => {
                setCourseValidity(e.target.checked)
                setHasUnsavedChanges(true)
              }}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-orange-600"></div>
          </label>
        </div>
      </div>

      {courseValidity && (
        <>
          {/* Period Selection */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center mb-3">
              <div className="bg-blue-100 rounded-full p-1.5 mr-2">
                <ClockIcon className="text-blue-600 w-4 h-4" />
              </div>
              <label className="text-base font-semibold text-gray-900">Access Duration</label>
            </div>
            <div className="relative">
              <select
                value={selectedPeriod}
                onChange={(e) => {
                  setSelectedPeriod(e.target.value)
                  setHasUnsavedChanges(true)
                }}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white text-gray-900 font-medium shadow-sm hover:border-gray-400 transition-colors text-sm"
              >
                {periods.map((period) => (
                  <option key={period} value={period}>
                    {period}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Custom Days Input */}
          {selectedPeriod === "Custom (in days)" && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <InputField
                type="number"
                label="Number of Days"
                name="customDays"
                value={customDays}
                onChange={(e) => {
                  setCustomDays(e.target.value)
                  setHasUnsavedChanges(true)
                }}
                placeholder="Enter number of days"
                className="text-sm"
              />
            </div>
          )}
        </>
      )}

{/* Pricing */}
<div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
  <div className="flex items-center mb-4">
    <div className="bg-purple-100 rounded-full p-1.5 mr-2">
      <ChartBarIcon className="text-purple-600 w-4 h-4" />
    </div>
    <h3 className="text-base font-semibold text-gray-900">
      Course Pricing Details
    </h3>
  </div>

  <div className="flex flex-col lg:flex-row gap-6">

    {/* =====================================================
        LEFT COLUMN - FORM
        ===================================================== */}
    <div className="flex-1 min-w-0">

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* ================= BASE PRICE ================= */}
        <div className="space-y-1.5 min-w-0">

          <InputField
            type="number"
            label="Base Price (₹)"
            name="basePrice"
            value={basePrice}
            onChange={(e) => {
              if (e.target.value && parseFloat(e.target.value) < 0) {
                return;
              }

              setBasePrice(e.target.value);
              setHasUnsavedChanges(true);
            }}
            placeholder="Enter base price"
            required
            className="text-sm [&_label]:whitespace-nowrap"
          />

          <p className="text-xs text-gray-500">
            Price before discounts and taxes
          </p>

        </div>


        {/* ================= SELLING PRICE ================= */}
        <div className="space-y-1.5 min-w-0">

          <label
            htmlFor="actualPrice"
            className="block text-xs font-semibold text-gray-800 whitespace-nowrap mb-3"
          >
            Selling Price (₹) <span className="text-red-500">*</span>
          </label>

          <input
            id="actualPrice"
            type="number"
            name="actualPrice"
            value={actualPrice}
            min="0"
            onChange={(e) => {
              if (e.target.value && parseFloat(e.target.value) < 0) {
                return;
              }

              setActualPrice(e.target.value);
              setHasUnsavedChanges(true);
            }}
            placeholder="Enter selling price"
            required
            className="block w-full h-[44px] px-4 py-2.5 border border-gray-200 rounded-lg bg-white text-gray-900 text-sm shadow-sm outline-none transition-all duration-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-100 hover:border-gray-300 placeholder:text-gray-400"
          />

          <p className="text-xs text-gray-500 mt-2">
            Final price students will pay
          </p>

        </div>


        {/* ================= GST RATE ================= */}
        <div className="space-y-1.5 min-w-0">

          <label className="block text-sm font-semibold text-gray-800 whitespace-nowrap">
            GST Rate
          </label>

          <div className="relative">

            <select
              value={selectedGstRate?._id || ""}
              onChange={(e) => {
                const rate = gstRates.find(
                  r => r._id === e.target.value
                );

                setSelectedGstRate(rate || null);
                setHasUnsavedChanges(true);
              }}
              className="block w-full h-[44px] px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 appearance-none bg-white shadow-sm hover:border-gray-400 transition-colors text-sm"
              disabled={loadingGstRates}
            >

              <option value="">
                Select GST Rate
              </option>

              {gstRates.map((rate) => (
                <option
                  key={rate._id}
                  value={rate._id}
                >
                  {rate.gstPercent}%
                </option>
              ))}

            </select>

            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg
                className="w-3 h-3 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

          </div>

          {loadingGstRates && (
            <p className="text-xs text-purple-500 mt-1 flex items-center">
              <span className="animate-spin rounded-full h-2.5 w-2.5 border-b-2 border-purple-500 mr-1" />
              Loading rates...
            </p>
          )}

        </div>

      </div>


      {/* ================= ACTION BUTTONS ================= */}
      <div className="mt-4 flex justify-end space-x-2">

        {editingIndex !== null ? (
          <>
            <Button
              variant="secondary"
              onClick={cancelEdit}
              className="flex items-center text-sm px-3 py-1.5"
            >
              Cancel
            </Button>

            <Button
              onClick={updatePriceDetail}
              className="flex items-center text-sm px-3 py-1.5"
            >
              Update Price Option
            </Button>
          </>
        ) : (
          <Button
            onClick={addPriceDetail}
            disabled={!hasValidFormData()}
            className="flex items-center text-sm px-3 py-1.5"
          >
            <PlusIcon className="w-3.5 h-3.5 mr-1.5" />
            Add Price Option
          </Button>
        )}

      </div>

    </div>


    {/* =====================================================
        RIGHT COLUMN - PLAN PREVIEW
        ===================================================== */}
    {editingIndex !== null && (
      <div className="lg:w-[360px] lg:flex-shrink-0">

        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">

          {/* Preview Heading */}
          <h3 className="text-base font-semibold text-gray-900 mb-3">
            Plan Preview
          </h3>


          {/* Preview Content */}
          <div className="bg-white rounded-lg p-4">

            {/* Base Price */}
            <div className="flex items-center justify-between gap-4 py-2.5">
              <span className="text-gray-600 font-medium whitespace-nowrap">
                Base Price:
              </span>

              <span className="font-semibold text-gray-900 whitespace-nowrap">
                ₹{basePrice || "0"}
              </span>
            </div>


            {/* Selling Price */}
            <div className="flex items-center justify-between gap-4 py-2.5">
              <span className="text-gray-600 font-medium whitespace-nowrap">
                Selling Price:
              </span>

              <span className="font-semibold text-gray-900 whitespace-nowrap">
                ₹{actualPrice || "0"}
              </span>
            </div>


            {/* GST */}
            <div className="flex items-center justify-between gap-4 py-2.5 border-b border-gray-100">

              <span className="text-gray-600 font-medium whitespace-nowrap">
                GST Amount ({selectedGstRate?.gstPercent || 0}%):
              </span>

              <span className="font-semibold text-gray-900 whitespace-nowrap">
                ₹{gstAmount || "0.00"}
              </span>

            </div>


            {/* ================= TOTAL ================= */}
            <div className="flex items-center justify-between gap-3 mt-3 py-4 px-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">

              <span className="text-blue-900 font-bold text-lg whitespace-nowrap">
                Total Amount:
              </span>

              <span className="text-blue-900 font-bold text-xl whitespace-nowrap">
                ₹{totalPrice.toFixed(2)}
              </span>

            </div>


            {/* Discount Message */}
            {parseFloat(actualPrice) < parseFloat(basePrice) && (
              <div className="flex items-center justify-center mt-3 p-2 bg-green-50 rounded-lg">

                <ExclamationCircleIcon className="text-green-600 w-4 h-4 mr-2 flex-shrink-0" />

                <span className="text-green-700 text-sm font-medium text-center">
                  Students save ₹
                  {(parseFloat(basePrice) - parseFloat(actualPrice)).toFixed(2)}
                  {" "}with this pricing!
                </span>

              </div>
            )}

          </div>

        </div>

      </div>
    )}

  </div>
</div>     

      {/* Price Options List */}
      {priceDetails.filter(detail => !detail._delete).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <h3 className="text-base font-semibold text-gray-900 mb-3">Price Options</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {priceDetails.map((detail, index) => (
              !detail._delete && (
                <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 hover:shadow-md transition-shadow duration-200">
                  {/* Plan Header */}
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-3 relative">
                    {detail.isBestDeal && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full flex items-center">
                        <StarIcon className="w-2.5 h-2.5 mr-0.5" />
                        Best Deal
                      </div>
                    )}
                    <h4 className="text-white font-bold text-base">
                      {detail.validityType === "custom"
                        ? `${detail.validityInDays} Days Plan`
                        : formatValidityType(detail.validityType)}
                    </h4>
                  </div>

                  {/* Plan Content */}
                  <div className="p-3">
                    <div className="mb-3">
                      <div className="flex items-baseline">
                        <span className="text-xl font-bold text-gray-900">₹{detail.actualPrice}</span>
                        {detail.price > detail.actualPrice && (
                          <span className="ml-1 text-sm text-gray-500 line-through">₹{detail.price}</span>
                        )}
                      </div>

                      {detail.price > detail.actualPrice && (
                        <div className="mt-1 text-xs text-green-600 font-medium">
                          Save {calculateDiscount(detail)}%
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center text-xs text-gray-600">
                        <ClockIcon className="w-3 h-3 mr-1 text-blue-500" />
                        <span>
                          {detail.validityType === "custom"
                            ? `${detail.validityInDays} days access`
                            : formatValidityType(detail.validityType)}
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-3 flex justify-end space-x-1">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => editPriceDetail(index)}
                        className="text-blue-600 hover:text-blue-800 p-1.5"
                      >
                        <PencilIcon className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => removePriceDetail(index)}
                        className="text-red-600 hover:text-red-800 p-1.5"
                      >
                        <TrashIcon className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      )}

      {/* Price Summary */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-center mb-3">
          <div className="bg-blue-100 rounded-full p-1.5 mr-2">
            <svg className="text-blue-600 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-gray-900">Pricing Summary</h3>
        </div>
        <div className="bg-white rounded-lg p-3 space-y-2">
          <div className="flex justify-between items-center py-1.5">
            <span className="text-gray-600 text-sm font-medium">Base Price:</span>
            <span className="font-semibold text-gray-900 text-sm">₹{basePrice || "0"}</span>
          </div>
          <div className="flex justify-between items-center py-1.5">
            <span className="text-gray-600 text-sm font-medium">Selling Price:</span>
            <span className="font-semibold text-gray-900 text-sm">₹{actualPrice || "0"}</span>
          </div>
          <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
            <span className="text-gray-600 text-sm font-medium">GST Amount ({selectedGstRate?.gstPercent || 0}%):</span>
            <span className="font-semibold text-gray-900 text-sm">₹{gstAmount || "0.00"}</span>
          </div>
          <div className="flex justify-between items-center py-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg px-3">
            <span className="text-blue-900 font-bold">Total Amount:</span>
            <span className="text-blue-900 font-bold">₹{totalPrice.toFixed(2)}</span>
          </div>
          {parseFloat(actualPrice) < parseFloat(basePrice) && (
            <div className="flex items-center justify-center mt-2 p-1.5 bg-green-50 rounded-lg">
              <ExclamationCircleIcon className="text-green-600 w-3.5 h-3.5 mr-1.5" />
              <span className="text-green-700 text-xs font-medium">
                Students save ₹{(parseFloat(basePrice) - parseFloat(actualPrice)).toFixed(2)} with this pricing!
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

export default PaymentDetailsStep